<template>
  <div class="globe-container" ref="container">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const canvas = ref(null);
const container = ref(null);
let ctx = null;
let width = 0;
let height = 0;
let animationFrameId = null;

// Globe configuration
let globeRadius = 250; // Initial, will be dynamic
const DOT_RADIUS = 2;
const DOT_COLOR = '#4aa3df';
const LINE_COLOR = 'rgba(74, 163, 223, 0.15)';
const ACTIVE_LINE_COLOR = 'rgba(74, 163, 223, 0.8)';
const ROTATION_SPEED = 0.0015; // Slightly slower for elegance
const PERSPECTIVE = 1000; // Increased perspective

// Particles (dots)
const particles = [];
const PARTICLE_COUNT = 800; // Increased count

// Lines (arcs)
const arcs = [];
const ARC_COUNT = 15;

class Particle {
  constructor() {
    this.init();
  }

  init() {
    this.theta = Math.random() * 2 * Math.PI;
    this.phi = Math.acos((Math.random() * 2) - 1);
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.x2d = 0;
    this.y2d = 0;
  }

  update(rotation) {
    let x = globeRadius * Math.sin(this.phi) * Math.cos(this.theta + rotation);
    let y = globeRadius * Math.cos(this.phi);
    let z = globeRadius * Math.sin(this.phi) * Math.sin(this.theta + rotation);

    this.x = x;
    this.y = y;
    this.z = z;

    const scale = PERSPECTIVE / (PERSPECTIVE + z);
    this.x2d = (x * scale) + width / 2;
    this.y2d = (y * scale) + height / 2;
    this.scale = scale;
  }

  draw() {
    if (this.z > -globeRadius + 50) {
      const alpha = (this.z + globeRadius) / (2 * globeRadius);
      ctx.fillStyle = DOT_COLOR;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(this.x2d, this.y2d, DOT_RADIUS * this.scale, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

class Arc {
  constructor() {
    this.reset();
  }

  reset() {
    this.p1 = particles[Math.floor(Math.random() * particles.length)];
    this.p2 = particles[Math.floor(Math.random() * particles.length)];
    this.progress = 0;
    this.speed = 0.005 + Math.random() * 0.01;
  }

  update() {
    this.progress += this.speed;
    if (this.progress >= 1) {
      this.reset();
    }
  }

  draw() {
    if (this.p1.z < -50 || this.p2.z < -50) return;

    const mx = (this.p1.x2d + this.p2.x2d) / 2;
    const my = (this.p1.y2d + this.p2.y2d) / 2;
    
    const cx = width / 2;
    const cy = height / 2;
    const dx = mx - cx;
    const dy = my - cy;
    
    // Higher curve for larger globe
    const cpX = cx + dx * 1.8;
    const cpY = cy + dy * 1.8;

    ctx.beginPath();
    ctx.moveTo(this.p1.x2d, this.p1.y2d);
    ctx.quadraticCurveTo(cpX, cpY, this.p2.x2d, this.p2.y2d);
    
    ctx.strokeStyle = ACTIVE_LINE_COLOR;
    ctx.lineWidth = 2;
    ctx.globalAlpha = Math.sin(this.progress * Math.PI);
    ctx.stroke();
  }
}

let rotation = 0;

const updateDimensions = () => {
  if (container.value) {
    width = container.value.clientWidth;
    height = container.value.clientHeight;
    canvas.value.width = width;
    canvas.value.height = height;
    
    // Responsive radius
    const minDim = Math.min(width, height);
    globeRadius = Math.max(200, Math.min(450, minDim * 0.35));
  }
};

const init = () => {
  updateDimensions();
  ctx = canvas.value.getContext('2d');

  particles.length = 0;
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    particles.push(new Particle());
  }

  arcs.length = 0;
  for (let i = 0; i < ARC_COUNT; i++) {
    arcs.push(new Arc());
  }

  animate();
};

const animate = () => {
  ctx.clearRect(0, 0, width, height);
  rotation += ROTATION_SPEED;

  particles.forEach(p => {
    p.update(rotation);
    p.draw();
  });

  arcs.forEach(a => {
    a.update();
    a.draw();
  });

  animationFrameId = requestAnimationFrame(animate);
};

const handleResize = () => {
  updateDimensions();
};

onMounted(() => {
  init();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  cancelAnimationFrame(animationFrameId);
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.globe-container {
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, #1a2a3a 0%, #020406 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
}

canvas {
  display: block;
}
</style>
